import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { UserX, UserCheck } from "lucide-react";

/**
 * Block / unblock button targeting a user. Backend endpoints:
 *  - POST   /api/users/{blocked_id}/block
 *  - DELETE /api/users/{blocked_id}/block
 * The list of currently-blocked users is read on mount from /me/blocks so
 * the button starts in the correct state without additional props.
 */
export function BlockUserButton({
  targetUserId, testid,
  size = "sm", variant = "outline", className = "",
}) {
  const { t } = useI18n();
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/blocks");
        if (cancelled) return;
        const items = data?.items || [];
        setBlocked(items.some((b) => b.blocked_id === targetUserId));
      } catch { /* silent — treat as unblocked */ }
    })();
    return () => { cancelled = true; };
  }, [targetUserId]);

  const doBlock = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${targetUserId}/block`, {});
      setBlocked(true);
      toast.success(t("block_user_ok"));
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setBusy(false); setConfirmOpen(false); }
  };

  const doUnblock = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${targetUserId}/block`);
      setBlocked(false);
      toast.success(t("unblock_user_ok"));
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setBusy(false); }
  };

  if (blocked) {
    return (
      <Button
        data-testid={testid || `unblock-user-btn-${targetUserId}`}
        variant={variant} size={size}
        disabled={busy}
        onClick={doUnblock}
        className={`rounded-full text-xs hover:bg-emerald-50 hover:text-emerald-800 ${className}`}
      >
        <UserCheck size={12} className="mr-1" /> {t("unblock_user_btn")}
      </Button>
    );
  }

  return (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogTrigger asChild>
        <Button
          data-testid={testid || `block-user-btn-${targetUserId}`}
          variant={variant} size={size}
          disabled={busy}
          className={`rounded-full text-xs hover:bg-red-50 hover:text-red-800 ${className}`}
        >
          <UserX size={12} className="mr-1" /> {t("block_user_btn")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="block-user-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("block_user_confirm_title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("block_user_confirm_desc")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="block-user-cancel">{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid="block-user-confirm-ok"
            onClick={doBlock}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {t("block_user_btn")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default BlockUserButton;
